import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllPurchases,
  getPurchaseById,
  addPurchase,
  updatePurchase,
  deletePurchase,
  getPurchasesByDateRange,
  getPurchasesBySupplier,
  getPurchaseStatistics,
  getSuppliers,
} from '../../services/purchaseService';

// Initial state
const initialState = {
  purchases: [],
  currentPurchase: null,
  suppliers: [],
  statistics: {
    totalAmount: 0,
    totalVolumePoints: 0,
    totalPurchases: 0,
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchPurchases = createAsyncThunk(
  'purchase/fetchPurchases',
  async (_, { rejectWithValue }) => {
    try {
      const purchases = await getAllPurchases();
      return purchases;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchaseById = createAsyncThunk(
  'purchase/fetchPurchaseById',
  async (purchaseId, { rejectWithValue }) => {
    try {
      const purchase = await getPurchaseById(purchaseId);
      return purchase;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPurchase = createAsyncThunk(
  'purchase/createPurchase',
  async (purchaseData, { rejectWithValue }) => {
    try {
      const purchaseId = await addPurchase(purchaseData);
      return { id: purchaseId, ...purchaseData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePurchaseById = createAsyncThunk(
  'purchase/updatePurchaseById',
  async ({ purchaseId, purchaseData }, { rejectWithValue }) => {
    try {
      await updatePurchase(purchaseId, purchaseData);
      return { id: purchaseId, ...purchaseData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePurchaseById = createAsyncThunk(
  'purchase/deletePurchaseById',
  async (purchaseId, { rejectWithValue }) => {
    try {
      await deletePurchase(purchaseId);
      return purchaseId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchasesByDateRange = createAsyncThunk(
  'purchase/fetchPurchasesByDateRange',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const purchases = await getPurchasesByDateRange(startDate, endDate);
      return purchases;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchasesBySupplier = createAsyncThunk(
  'purchase/fetchPurchasesBySupplier',
  async (supplierId, { rejectWithValue }) => {
    try {
      const purchases = await getPurchasesBySupplier(supplierId);
      return purchases;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchaseStatistics = createAsyncThunk(
  'purchase/fetchPurchaseStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const statistics = await getPurchaseStatistics();
      return statistics;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSuppliers = createAsyncThunk(
  'purchase/fetchSuppliers',
  async (_, { rejectWithValue }) => {
    try {
      const suppliers = await getSuppliers();
      return suppliers;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const purchaseSlice = createSlice({
  name: 'purchase',
  initialState,
  reducers: {
    clearCurrentPurchase: (state) => {
      state.currentPurchase = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all purchases
      .addCase(fetchPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload;
      })
      .addCase(fetchPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch purchase by ID
      .addCase(fetchPurchaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPurchase = action.payload;
      })
      .addCase(fetchPurchaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create purchase
      .addCase(createPurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = [action.payload, ...state.purchases];
      })
      .addCase(createPurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update purchase
      .addCase(updatePurchaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePurchaseById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.purchases.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.purchases[index] = action.payload;
        }
      })
      .addCase(updatePurchaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete purchase
      .addCase(deletePurchaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePurchaseById.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = state.purchases.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePurchaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch purchases by date range
      .addCase(fetchPurchasesByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchasesByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload;
      })
      .addCase(fetchPurchasesByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch purchases by supplier
      .addCase(fetchPurchasesBySupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchasesBySupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload;
      })
      .addCase(fetchPurchasesBySupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch purchase statistics
      .addCase(fetchPurchaseStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchPurchaseStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentPurchase, clearError } = purchaseSlice.actions;
export default purchaseSlice.reducer;
